import { useState } from 'react';
import type { MediaStoragePageResponse } from '@/core/generated/api';
import { mediaLibraryService } from '@/entities/MediaLibrary/api/mediaLibraryService';
import { formatMediaSize } from '@/entities/MediaLibrary/model/format';
import { getErrorMessage } from '@/shared/lib/errors';
import { Modal } from '@/shared/ui/Modal/Modal';
import { InputText } from '@/shared/ui/InputText/InputText';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { RequestError } from '@/shared/ui/RequestState';
import classes from './MediaLibrary.module.scss';

interface MediaImportDialogProps {
    folderId?: string;
    onClose: () => void;
    onImported: () => void;
}

/** Проверяет чтение S3 и импортирует выбранные изображения */
export const MediaImportDialog = ({ folderId, onClose, onImported }: MediaImportDialogProps) => {
    const [prefix, setPrefix] = useState('images/');
    const [page, setPage] = useState<MediaStoragePageResponse>();
    const [selected, setSelected] = useState<string[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [imported, setImported] = useState<number>();

    /** Проверяет доступ и читает страницу объектов заданного префикса */
    const scan = async (continuationToken?: string): Promise<void> => {
        setBusy(true);
        setError('');
        setSelected([]);

        try {
            setPage(await mediaLibraryService.storage(prefix, continuationToken));
        } catch (failure: unknown) {
            setPage(undefined);
            setError(getErrorMessage(failure, 'Не удалось прочитать S3. Проверьте ключи и права окружения.'));
        } finally {
            setBusy(false);
        }
    };

    /** Индексирует выбранные изображения без повторной загрузки в S3 */
    const importFiles = async (): Promise<void> => {
        setBusy(true);
        setError('');

        try {
            const count = await mediaLibraryService.import({
                keys: selected,
                folderId: folderId ?? null
            });

            setImported(count);

            setPage(previous => previous
                ? {
                    ...previous,
                    items: previous.items?.map(item => selected.includes(item.key ?? '')
                        ? {
                            ...item,
                            isImported: true
                        }
                        : item)
                }
                : previous);

            setSelected([]);
            onImported();
        } catch (failure: unknown) {
            setError(getErrorMessage(failure, 'Не удалось импортировать изображения'));
        } finally {
            setBusy(false);
        }
    };

    return <Modal
        appearance="surface"
        visible
        title="Подключение к S3 и импорт файлов"
        onHide={onClose}
        closable={!busy}
        size="lg"
    >
        <div className={classes.dialogForm}>
            <div className={classes.notice}>
                <i
                    aria-hidden="true"
                    className="pi pi-cloud"
                />
                <div>
                    <strong>Хранилище текущего окружения</strong>
                    <small>
                        {page?.bucket ?? 'Бакет определяет backend'}
                    </small>
                </div>
                <ActionButton
                    type="button"
                    outlined
                    color="secondary"
                    loading={busy}
                    onClick={() => void scan()}
                >
                    Проверить подключение
                </ActionButton>
            </div>
            {page &&
                <p className={classes.connection}>
                    <i
                        aria-hidden="true"
                        className="pi pi-check-circle"
                    /> Чтение S3 доступно</p>
            }
            <p className={classes.note}>Существующие изображения появятся в медиатеке без копирования и изменения ссылок. Доступ на загрузку проверяется самой загрузкой; ключи не передаются браузеру.</p>
            <InputText
                name="media-import-prefix"
                label="Префикс S3"
                value={prefix}
                onChange={event => {
                    setPrefix(event.target.value);
                    setPage(undefined);
                    setSelected([]);
                }}
                placeholder="images/"
                disabled={busy}
            />
            <div className={classes.importList}>
                {page?.items?.map(item => <label
                    key={item.key}
                    className={classes.importRow}
                >
                    <input
                        type="checkbox"
                        checked={selected.includes(item.key ?? '')}
                        disabled={busy || item.isImported || selected.length >= 50 && !selected.includes(item.key ?? '')}
                        onChange={event => setSelected(previous => event.target.checked
                            ? [...previous, item.key ?? '']
                            : previous.filter(key => key !== item.key))}
                    />
                    <i
                        aria-hidden="true"
                        className="pi pi-image"
                    />
                    <span>
                        {item.key}
                        <small>
                            {formatMediaSize(item.size)}
                        </small>
                    </span>
                    {item.isImported &&
                        <small className={classes.connection}>В медиатеке</small>
                    }
                </label>)}
            </div>
            {page && !page.items?.length &&
                <p className={classes.note}>На этой странице изображений нет. Можно продолжить просмотр или изменить префикс.</p>
            }
            {page?.continuationToken &&
                <ActionButton
                    type="button"
                    outlined
                    color="secondary"
                    disabled={busy}
                    onClick={() => void scan(page.continuationToken ?? undefined)}
                >
                    Следующая страница S3
                </ActionButton>
            }
            {imported !== undefined &&
                <p
                    role="status"
                    className={classes.connection}
                >
                    Добавлено файлов: {imported}
                </p>
            }
            {error &&
                <RequestError message={error} />
            }
            <div className={classes.dialogFooter}>
                <ActionButton
                    type="button"
                    outlined
                    color="secondary"
                    disabled={busy}
                    onClick={onClose}
                >
                    Закрыть
                </ActionButton>
                <ActionButton
                    type="button"
                    icon="pi pi-download"
                    loading={busy}
                    disabled={!selected.length}
                    onClick={() => void importFiles()}
                >
                    Импортировать ({selected.length})
                </ActionButton>
            </div>
        </div>
    </Modal>;
};
